import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Eye, Loader2, Save } from 'lucide-react';
import { Button } from '@/components/ui';
import { FieldRenderer } from '../fields/FieldRenderer';
import { FieldChromeProvider } from '../fields/FieldShell';
import { resolveWidgetKind } from '../helpers';
import { validateField } from '../validators';
import type {
  FormField,
  FormSchema,
  GridSubmissionRow,
  GridSubmitPayload,
} from '../types';
import { DocumentPreviewModal } from '../../components/DocumentPreviewModal';
import {
  headerLabel,
  idValueForRow,
  indexSubmissionsByRowKey,
  isLabelColumn,
  isVersionedDocumentViewer,
  readOptionValue,
  resolveGridColumns,
  rowKeyFromOption,
} from './helpers';

/**
 * Spec: docs/onboarding/API/Grid.md
 *
 * Renders a `grid_input` form as a table:
 *   - One row per `schema.options` entry.
 *   - `grid_filled` items → read-only cells (label / document viewer / id).
 *   - `default` items     → editable inputs (re-uses `FieldRenderer`).
 *   - Per-row "Save" persists via `POST /form/:form_name`.
 *   - Bottom "Submit" persists any unsaved rows then completes the workflow
 *     with one row's payload (spec §8).
 */

interface Props {
  schema: FormSchema;
  /** Workflow entity id (case id) — used as the submission_id in §6.2/§6.3. */
  entityId: number | string;
  /**
   * Loader for prior per-row submissions. The modal wires this to
   * `onboardingService.getGridSubmissions(formName, submissionId)`.
   */
  loadSubmissions: (
    formName: string,
    submissionId: number | string,
  ) => Promise<GridSubmissionRow[]>;
  /**
   * Save one row. Wired to `onboardingService.saveGridRow(formName, body)`.
   * Throws on server failure so the row can show a per-row error.
   */
  saveRow: (formName: string, body: Record<string, unknown>) => Promise<unknown>;
  /**
   * Fetch a presigned URL for a `versioned_document_viewer` cell.
   * Wired to `onboardingService.getDocumentVersion(...)`.
   */
  fetchDocumentVersion: (versionId: number) => Promise<{ url: string }>;
  /** Workflow event POST — the modal binds this to its submitMutation. */
  onSubmit: (payload: GridSubmitPayload) => void | Promise<unknown>;
  onCancel?: () => void;
  submitting?: boolean;
  submitLabel?: string;
  serverError?: string | null;
  /**
   * Lookup-derived body keys the modal contributes (e.g. `filled_by` from
   * the auth user). Merged into both per-row saves and the workflow body.
   */
  lookupAdditions?: Record<string, unknown>;
}

interface RowState {
  values: Record<string, unknown>;
  errors: Record<string, string>;
  saved: boolean;
  saving: boolean;
  saveError: string | null;
}

/**
 * Build the per-row editable state.
 *
 * Two categories of `default` items are skipped from `values` so they
 * don't end up in the save / workflow body:
 *
 *   1. Versioned-document viewers — these are action links, not inputs.
 *      Their `title` often collides with the ID column's title (both
 *      `document_version_id` in `candidate_document_review`), and the
 *      empty `''` would silently overwrite the row id in the body. Skip
 *      them entirely; render handles the View link separately.
 *   2. Label columns — read-only displays that mirror an `options` value.
 *      Pre-fill from `options` so the value still round-trips to the
 *      server (label columns are commonly used for audit fields like
 *      `reviewer` that the backend wants echoed in the save body).
 */
/**
 * Move free-text "long" columns (comments / reasons / notes / remarks)
 * to the end of the `default` row so the more compact inputs and the
 * read-only label columns stay grouped together on the left. Stable in
 * the sense that columns that don't match the heuristic keep their
 * original relative order.
 */
const LONG_TEXT_COLUMN_PATTERN = /(?:^|_)(comments?|reasons?|rejection_reason|remarks?|notes?|description)(?:$|_)/i;
const isLongTextColumn = (col: FormField): boolean =>
  LONG_TEXT_COLUMN_PATTERN.test(col.title || '');

const sortDefaultColumns = (items: FormField[]): FormField[] => {
  const head: FormField[] = [];
  const tail: FormField[] = [];
  for (const it of items) (isLongTextColumn(it) ? tail : head).push(it);
  return [...head, ...tail];
};

const blankRow = (
  defaultItems: FormField[],
  option: Record<string, unknown>,
  submission?: GridSubmissionRow,
): RowState => {
  const values: Record<string, unknown> = {};
  for (const f of defaultItems) {
    if (isVersionedDocumentViewer(f)) continue;
    if (isLabelColumn(f)) {
      // Label columns can be sourced either from a previous submission
      // (e.g. `reviewer` recorded when the row was saved earlier) or from
      // the master option row. Prefer submission so an already-acted row
      // shows the reviewer who handled it; fall back to option for static
      // server-side context like document name.
      const fromSubmission = submission?.[f.title];
      if (fromSubmission !== undefined && fromSubmission !== null && fromSubmission !== '') {
        values[f.title] = fromSubmission;
      } else {
        const v = readOptionValue(option, f);
        if (v !== undefined && v !== null && v !== '') values[f.title] = v;
      }
      continue;
    }
    const fromSubmission = submission?.[f.title];
    values[f.title] = fromSubmission ?? '';
  }
  return {
    values,
    errors: {},
    saved: !!submission,
    saving: false,
    saveError: null,
  };
};

export const GridInputForm = ({
  schema,
  entityId,
  loadSubmissions,
  saveRow,
  fetchDocumentVersion,
  onSubmit,
  onCancel,
  submitting = false,
  submitLabel = 'Submit',
  serverError = null,
  lookupAdditions = {},
}: Props) => {
  const { gridFilled, defaultItems: rawDefaultItems, idColumn } = useMemo(
    () => resolveGridColumns(schema),
    [schema],
  );
  // Reorder so wordy columns (comments / rejection reason) go last.
  const defaultItems = useMemo(() => sortDefaultColumns(rawDefaultItems), [rawDefaultItems]);
  const options = schema.options ?? [];
  const formName = schema.name ?? '';

  // ── Hydrate from server-supplied per-row submissions ────────────────────
  const submissionsQuery = useQuery({
    queryKey: ['grid-submissions', formName, entityId],
    queryFn: () => loadSubmissions(formName, entityId),
    enabled: !!formName && !!idColumn && options.length > 0,
    staleTime: 30 * 1000,
  });

  const submissionsByRow = useMemo(
    () => (idColumn ? indexSubmissionsByRowKey(submissionsQuery.data, idColumn) : new Map()),
    [submissionsQuery.data, idColumn],
  );

  // ── Per-row state ───────────────────────────────────────────────────────
  const [rowStates, setRowStates] = useState<Record<string, RowState>>({});
  const [workflowError, setWorkflowError] = useState<string | null>(null);

  // (Re)build row state whenever options or submissions arrive. We deliberately
  // reset on every change so server data wins over stale local state — users
  // should retry edits if a fresh load races them.
  useEffect(() => {
    if (!idColumn) {
      setRowStates({});
      return;
    }
    const next: Record<string, RowState> = {};
    for (const opt of options) {
      const key = rowKeyFromOption(opt, idColumn);
      if (key === null) continue;
      next[key] = blankRow(defaultItems, opt, submissionsByRow.get(key));
    }
    setRowStates(next);
  }, [options, defaultItems, idColumn, submissionsByRow]);

  // ── Document preview state ─────────────────────────────────────────────
  // The modal handles its own data fetch via the shared `getDocumentVersion`
  // service, so the grid only needs to track which version is open and what
  // label to show in the modal title.
  const [previewState, setPreviewState] = useState<{
    versionId: number;
    title: string;
  } | null>(null);

  const openPreview = (versionId: number, title: string) => {
    setPreviewState({ versionId, title });
  };
  // Acknowledge the prop so callers can still pass it; we now use the
  // shared modal which calls `getDocumentVersion` internally. The
  // `fetchDocumentVersion` prop is kept for API stability — modal
  // wraps it transparently.
  void fetchDocumentVersion;

  // ── Cell editing ────────────────────────────────────────────────────────
  const setCellValue = (rowKey: string, title: string, value: unknown) => {
    setRowStates((prev) => {
      const row = prev[rowKey];
      if (!row) return prev;
      const errors = { ...row.errors };
      delete errors[title];
      return {
        ...prev,
        [rowKey]: { ...row, values: { ...row.values, [title]: value }, errors, saved: false },
      };
    });
  };

  const validateRow = (rowKey: string): boolean => {
    const row = rowStates[rowKey];
    if (!row) return false;
    const errors: Record<string, string> = {};
    for (const f of defaultItems) {
      // Viewer + label cells aren't user input; validating them would
      // either be a no-op (label values come from `option`) or spuriously
      // flag the action-link column as "required but empty".
      if (isVersionedDocumentViewer(f) || isLabelColumn(f)) continue;
      const err = validateField(f, row.values[f.title]);
      if (err) errors[f.title] = err;
    }
    setRowStates((prev) => ({ ...prev, [rowKey]: { ...prev[rowKey], errors } }));
    return Object.keys(errors).length === 0;
  };

  const buildRowPayload = (rowKey: string): Record<string, unknown> | null => {
    if (!idColumn) return null;
    const option = options.find((o) => rowKeyFromOption(o, idColumn) === rowKey);
    if (!option) return null;
    const idValue = idValueForRow(option, idColumn);
    if (idValue === null) return null;
    const row = rowStates[rowKey];
    if (!row) return null;
    // Spec §6.3: body MUST include the property named after the id column's
    // `title` AND the literal `grid_filled` key, both set to the row's id
    // value (NOT the workflow entity id). `lookupAdditions` resolves
    // `body.*` lookups to `entityId` by default — that's right for
    // `entity_id` / `onb_site_id` / `candidate_case_id` style references,
    // but wrong for `grid_filled`, so we override it last.
    return {
      [idColumn.title]: idValue,
      entity_id: entityId,
      ...row.values,
      ...lookupAdditions,
      grid_filled: idValue,
    };
  };

  const saveSingleRow = async (rowKey: string): Promise<boolean> => {
    if (!formName) return false;
    if (!validateRow(rowKey)) return false;
    const payload = buildRowPayload(rowKey);
    if (!payload) return false;
    setRowStates((prev) => ({
      ...prev,
      [rowKey]: { ...prev[rowKey], saving: true, saveError: null },
    }));
    try {
      await saveRow(formName, payload);
      setRowStates((prev) => ({
        ...prev,
        [rowKey]: { ...prev[rowKey], saving: false, saved: true, saveError: null },
      }));
      return true;
    } catch (err: any) {
      setRowStates((prev) => ({
        ...prev,
        [rowKey]: {
          ...prev[rowKey],
          saving: false,
          saveError: err?.message || 'Save failed.',
        },
      }));
      return false;
    }
  };

  const handleSubmitAll = async () => {
    if (submitting) return;
    if (!idColumn || !formName) return;

    // Validate every row first so the user sees all blockers at once
    // rather than discovering them one by one as saves fail.
    const rowKeys = Object.keys(rowStates);
    let allValid = true;
    for (const rowKey of rowKeys) {
      if (!validateRow(rowKey)) allValid = false;
    }
    if (!allValid) {
      setWorkflowError('Some rows have validation errors. Fix and try again.');
      return;
    }
    setWorkflowError(null);

    // Persist any rows not yet saved. Saves run in parallel — order doesn't
    // matter because each row is independent on the server.
    const unsavedKeys = rowKeys.filter((k) => !rowStates[k].saved);
    if (unsavedKeys.length > 0) {
      const results = await Promise.all(unsavedKeys.map((k) => saveSingleRow(k)));
      if (results.some((ok) => !ok)) {
        setWorkflowError('One or more rows failed to save. See per-row errors.');
        return;
      }
    }

    // Workflow POST: spec §8 — any single row payload is enough. Use the
    // first row so the choice is deterministic.
    const firstRowKey = rowKeys[0];
    const payload = firstRowKey ? buildRowPayload(firstRowKey) : null;
    if (!payload) {
      setWorkflowError('No row data available to complete the step.');
      return;
    }
    try {
      await onSubmit(payload);
    } catch (err: any) {
      setWorkflowError(err?.message || 'Could not complete the workflow step.');
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────
  if (!idColumn) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        This grid is misconfigured — the schema does not declare an{' '}
        <code className="font-mono">uidt:&quot;ID&quot;</code> column. Please contact support.
      </div>
    );
  }

  const isLoading = submissionsQuery.isLoading;
  const showNoRows = !isLoading && options.length === 0;

  return (
    <div className="space-y-4">
      {showNoRows ? (
        <div className="card">
          <div className="card-body text-center text-rgf-gray-500">No items to review.</div>
        </div>
      ) : (
        <div className="card">
          <div className="card-body p-0">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-rgf-gray-200">
                <thead className="bg-rgf-gray-50">
                  <tr>
                    {gridFilled.map((col) => (
                      <th key={col.title} className="table-header">
                        {headerLabel(col)}
                      </th>
                    ))}
                    {defaultItems.map((col) => (
                      <th key={col.title} className="table-header">
                        {headerLabel(col)}
                        {col.rqd ? <span className="text-red-600"> *</span> : null}
                      </th>
                    ))}
                    <th className="table-header text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-rgf-gray-100">
                  {isLoading && (
                    <tr>
                      <td
                        colSpan={gridFilled.length + defaultItems.length + 1}
                        className="table-cell text-center text-rgf-gray-500"
                      >
                        Loading existing reviews…
                      </td>
                    </tr>
                  )}
                  {!isLoading &&
                    options.map((option) => {
                      const rowKey = rowKeyFromOption(option, idColumn);
                      if (rowKey === null) return null;
                      const row = rowStates[rowKey];
                      return (
                        <tr key={rowKey} className="hover:bg-rgf-gray-50 align-top">
                          {gridFilled.map((col) => (
                            <td key={col.title} className="table-cell">
                              {renderGridFilledCell({
                                col,
                                option,
                                idColumn,
                                openPreview,
                              })}
                            </td>
                          ))}
                          {defaultItems.map((col) => (
                            <td
                              key={col.title}
                              className="px-6 py-4 text-sm text-rgf-gray-900 border-b border-rgf-gray-100 align-top min-w-[180px]"
                            >
                              {/* Viewer + label columns can appear in either
                                  group — spec §3/§4. Render them read-only.
                                  Long-text inputs (comments / reasons) get
                                  click-to-edit so the grid isn't a wall of
                                  empty boxes. Everything else uses the
                                  shared FieldRenderer. */}
                              {isVersionedDocumentViewer(col) ? (
                                renderViewerLink({ col, option, idColumn, openPreview })
                              ) : isLabelColumn(col) ? (
                                <span className="text-sm text-rgf-gray-700">
                                  {formatCellValue(
                                    row?.values[col.title] ?? readOptionValue(option, col),
                                  )}
                                </span>
                              ) : row && resolveWidgetKind(col) === 'SingleLineText' ? (
                                <EditableTextCell
                                  value={
                                    typeof row.values[col.title] === 'string'
                                      ? (row.values[col.title] as string)
                                      : ''
                                  }
                                  error={row.errors[col.title]}
                                  required={!!col.rqd}
                                  onCommit={(next) => setCellValue(rowKey, col.title, next)}
                                />
                              ) : row ? (
                                // Suppress the per-cell label — the column
                                // header already names the field, and a
                                // repeated "Action:" / "Reviewer:" above
                                // every dropdown is just noise.
                                <FieldChromeProvider hideLabel>
                                  <FieldRenderer
                                    field={col}
                                    value={row.values[col.title]}
                                    error={row.errors[col.title]}
                                    onChange={(next) => setCellValue(rowKey, col.title, next)}
                                  />
                                </FieldChromeProvider>
                              ) : null}
                            </td>
                          ))}
                          <td className="table-cell text-right">
                            <RowSaveIcon
                              saving={!!row?.saving}
                              saved={!!row?.saved}
                              disabled={!row || submitting}
                              error={row?.saveError ?? null}
                              onClick={() => saveSingleRow(rowKey)}
                            />
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {(workflowError || serverError) && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {workflowError || serverError}
        </div>
      )}

      <div className="flex items-center justify-end gap-2">
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
        )}
        <Button
          type="button"
          disabled={submitting || isLoading || options.length === 0}
          onClick={handleSubmitAll}
        >
          {submitting ? 'Submitting…' : submitLabel}
        </Button>
      </div>

      <DocumentPreviewModal
        entityType="case"
        entityId={entityId}
        versionId={previewState?.versionId ?? null}
        title={previewState?.title}
        onClose={() => setPreviewState(null)}
      />
    </div>
  );
};

// ── Click-to-edit text cell ───────────────────────────────────────────────

interface EditableTextCellProps {
  value: string;
  error?: string;
  required?: boolean;
  placeholder?: string;
  onCommit: (next: string) => void;
}

/**
 * Compact cell that shows the value as plain text and switches to an
 * input on click. Enter / blur commit; Escape reverts. Used for wordy
 * text columns (comments / rejection reason) so the grid isn't a wall
 * of empty boxes.
 */
const EditableTextCell = ({
  value,
  error,
  required,
  placeholder,
  onCommit,
}: EditableTextCellProps) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const commit = () => {
    if (draft !== value) onCommit(draft);
    setEditing(false);
  };
  const cancel = () => {
    setDraft(value);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="space-y-1">
        <input
          ref={inputRef}
          type="text"
          className="form-input w-full"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              cancel();
            }
          }}
        />
        {error && <p className="text-xs text-red-600">{error}</p>}
      </div>
    );
  }

  const empty = value === '' || value === undefined || value === null;
  return (
    <button
      type="button"
      className="text-left w-full min-h-[1.75rem] px-2 py-1 -mx-2 rounded text-sm text-rgf-gray-900 hover:bg-rgf-gray-100 transition-colors break-words"
      onClick={() => setEditing(true)}
    >
      {empty ? (
        <span className="italic text-rgf-gray-400">
          {placeholder || (required ? 'Click to add (required)' : 'Click to add')}
        </span>
      ) : (
        value
      )}
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </button>
  );
};

// ── Per-row save action ────────────────────────────────────────────────────

interface RowSaveIconProps {
  saving: boolean;
  saved: boolean;
  disabled: boolean;
  error: string | null;
  onClick: () => void;
}

const RowSaveIcon = ({ saving, saved, disabled, error, onClick }: RowSaveIconProps) => {
  const title = saving
    ? 'Saving…'
    : error
    ? error
    : saved
    ? 'Saved — click to update'
    : 'Save row';
  return (
    <button
      type="button"
      aria-label={title}
      title={title}
      disabled={disabled || saving}
      onClick={onClick}
      className={`inline-flex items-center justify-center h-8 w-8 rounded-md transition-colors ${
        disabled || saving
          ? 'text-rgf-gray-400'
          : saved
          ? 'text-emerald-600 hover:bg-emerald-50'
          : error
          ? 'text-red-600 hover:bg-red-50'
          : 'text-rgf-primary-700 hover:bg-rgf-primary-50'
      }`}
    >
      {saving ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : saved ? (
        <Check className="h-4 w-4" />
      ) : (
        <Save className="h-4 w-4" />
      )}
    </button>
  );
};

// ── Row cell rendering ─────────────────────────────────────────────────────

const ISO_DATETIME_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/**
 * Render a cell value for display.
 *
 *   - ISO datetimes (e.g. `2026-05-19T05:14:07.000Z`) are formatted in IST
 *     for the local audience — `19 May 2026, 10:44 am` — regardless of
 *     the browser's timezone, so two reviewers on different machines see
 *     the same value.
 *   - Everything else round-trips as a string.
 */
const formatCellValue = (v: unknown): string => {
  if (v === undefined || v === null) return '';
  if (typeof v === 'string' && ISO_DATETIME_PATTERN.test(v)) {
    const d = new Date(v);
    if (!Number.isNaN(d.getTime())) {
      return new Intl.DateTimeFormat('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Asia/Kolkata',
        hour12: true,
      }).format(d);
    }
  }
  return String(v);
};

/**
 * Best label to identify a row's document in the preview-modal title.
 * Tries the row's own `name` / `document_name`, then the column's
 * display title — falls back to "Document".
 */
const previewTitleForRow = (option: Record<string, unknown>, col: FormField): string => {
  for (const key of ['name', 'document_name', 'display_title']) {
    const v = option[key];
    if (typeof v === 'string' && v.length > 0) return v;
  }
  return col.display_title || col.title || 'Document';
};

/**
 * Render the "View" hyperlink that opens the preview modal. Shared
 * between `grid_filled` and `default` since viewer columns can show
 * up in either group depending on the resource.
 */
const renderViewerLink = ({
  col,
  option,
  idColumn,
  openPreview,
}: {
  col: FormField;
  option: Record<string, unknown>;
  idColumn: FormField;
  openPreview: (versionId: number, title: string) => void;
}) => {
  // Per spec §4: the row exposes `document_version_id`. In practice this
  // coincides with the `uidt:"ID"` column's value. Prefer reading via the
  // id column (resource-agnostic re: title vs column_name), then fall
  // back to the column's own value, then the documented literal.
  const raw =
    readOptionValue(option, idColumn) ??
    readOptionValue(option, col) ??
    option.document_version_id;
  const versionId = Number(raw);
  if (!Number.isFinite(versionId) || versionId <= 0) {
    return <span className="text-rgf-gray-400 italic">No document</span>;
  }
  const title = previewTitleForRow(option, col);
  return (
    <button
      type="button"
      className="inline-flex items-center gap-1 text-rgf-primary-700 hover:text-rgf-primary-800 hover:underline font-medium"
      onClick={() => openPreview(versionId, title)}
    >
      <Eye className="h-3.5 w-3.5" />
      View
    </button>
  );
};

const renderGridFilledCell = ({
  col,
  option,
  idColumn,
  openPreview,
}: {
  col: FormField;
  option: Record<string, unknown>;
  idColumn: FormField;
  openPreview: (versionId: number, title: string) => void;
}) => {
  if (isVersionedDocumentViewer(col)) {
    return renderViewerLink({ col, option, idColumn, openPreview });
  }
  if (isLabelColumn(col)) {
    return <span>{formatCellValue(readOptionValue(option, col))}</span>;
  }
  // Fallback: spec §3 calls this group "read-only or effectively read-only".
  return <span>{formatCellValue(readOptionValue(option, col))}</span>;
};
